import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import qs from 'query-string';
import { appEndpoint, workflowRunEndpoint } from '@/helpers/apiHelpers';
import { RootState } from '../reducers/reducers';
import { TimelineEventsEntityRequest, TimelineEventsHistoryEntityRequest } from '@/actions/entities';
import { TimelineEventsHistoryPaginatedResponse } from '@/actions/timelineEventsHistory';

export interface TimelineEventsAPIRequest extends TimelineEventsHistoryEntityRequest, TimelineEventsEntityRequest {}
export interface PaginationAPIResponse<T> {
    data: T;
    errorMessage?: string;
    success: boolean;
}

export const paginationApi = createApi({
    reducerPath: 'paginationApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/',
        prepareHeaders: (headers, { getState }) => {
            const state = getState() as RootState;
            const auth = state.auth?.authCredentials;
            if (auth?.token) {
                headers.set('authorization', `Bearer ${auth.token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['TimelineEvents'],
    endpoints: (builder) => ({
        getTimelineEventsHistory: builder.query<
            PaginationAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({ orgId, appId, releaseId, releaseStepType, nextCursor }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        releaseId: releaseId ?? '',
                        releaseStepType: releaseStepType ?? '',
                        timelineEventsCursor: nextCursor ?? '',
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/timelineEventsHistory?${encodedQueryParams}`;
            },
            providesTags: (result, error, { orgId, appId, releaseId, releaseStepType }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${appId}-${releaseId || 'all'}-${releaseStepType || 'all'}`,
                },
            ],
        }),
        getTimelineEvents: builder.query<
            PaginationAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({ orgId, workflowId, workflowRunId, stepRunIds, nextCursor }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        stepRunIds: stepRunIds ?? [],
                        timelineEventsCursor: nextCursor ?? '',
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const workflowUrl = workflowRunEndpoint({ orgId, workflowId, workflowRunId });
                return `${workflowUrl}/timelineEvents?${encodedQueryParams}`;
            },
            providesTags: (result, error, { orgId, workflowId, workflowRunId }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${workflowId}-${workflowRunId || 'all'}`,
                },
            ],
        }),
    }),
});

export const {
    useGetTimelineEventsHistoryQuery,
    useGetTimelineEventsQuery,
    useLazyGetTimelineEventsHistoryQuery,
    useLazyGetTimelineEventsQuery,
} = paginationApi;
